import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  House, User, Wrench, FolderSimple, StackSimple, Notebook,
  EnvelopeSimple, CaretRight, Sun, Moon, LinkedinLogo, XLogo,
  Article, MediumLogo, ArrowUpRight, InstagramLogo, YoutubeLogo,
} from "@phosphor-icons/react";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import { useTheme } from "@/context/ThemeContext";
import { usePage } from "@/context/PageContext";
import { SERVICE_MENU_ITEMS, STACK_ITEMS, PROJECT_ITEMS } from "@/lib/nav";

function useNav() {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const isActive = (key, params) => {
    const p = buildPath(key, lang, params || {});
    return pathname === p;
  };
  const link = (key, params) => buildPath(key, lang, params || {});
  return { lang, isActive, link, pathname };
}

function TopLink({ to, active, icon: Icon, label, testid, onNavigate }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      onClick={onNavigate}
      className={`relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
        active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {active && (
        <motion.span
          layoutId="side-active"
          className="absolute inset-0 rounded-md bg-secondary border border-border"
          transition={{ type: "spring", stiffness: 500, damping: 40 }}
        />
      )}
      <Icon weight={active ? "fill" : "regular"} size={18} className="relative z-10 shrink-0" />
      <span className="relative z-10">{label}</span>
    </Link>
  );
}

function Section({ icon: Icon, label, hubTo, hubLabel, items, routeKey, lang, isActive, onNavigate, testid, open, onToggle }) {
  return (
    <div>
      <button
        onClick={onToggle}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <Icon size={18} className="shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        <motion.span animate={{ rotate: open ? 90 : 0 }} className="block">
          <CaretRight size={14} weight="bold" />
        </motion.span>
      </button>
      {/*
        SEO note: We always render the full list of child links in the DOM so
        crawlers (Googlebot, GPTBot, ClaudeBot, etc.) can discover every
        service, project and tech-stack page from any route — not only the
        ones inside the currently-expanded accordion. Visual collapse is
        purely a CSS/height animation, not a conditional mount.
      */}
      <motion.ul
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        aria-hidden={!open}
        className="overflow-hidden pl-6"
      >
        <li>
          <Link
            to={hubTo}
            onClick={onNavigate}
            data-testid={testid}
            tabIndex={open ? 0 : -1}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-[13px] transition-colors ${
              isActive(routeKey === "service" ? "services" : routeKey === "project" ? "projects" : "techstacks")
                ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className="h-1 w-1 rounded-full bg-border" />
            {hubLabel}
          </Link>
        </li>
        {items.map((it) => {
          const active = isActive(routeKey, { slug: it.slug });
          return (
            <li key={it.slug}>
              <Link
                to={buildPath(routeKey, lang, { slug: it.slug })}
                onClick={onNavigate}
                data-testid={`nav-${routeKey}-${it.slug}`}
                tabIndex={open ? 0 : -1}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-[13px] transition-colors ${
                  active ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <span className={`h-1 w-1 rounded-full ${active ? "bg-foreground" : "bg-border"}`} />
                {it[lang]}
              </Link>
            </li>
          );
        })}
      </motion.ul>
    </div>
  );
}

export function SidebarContent({ profile, onNavigate }) {
  const { lang, isActive, link } = useNav();
  const { t, other } = useLang();
  const { theme, toggle } = useTheme();
  const { page } = usePage();
  const navigate = useNavigate();

  const activeDefault = SERVICE_MENU_ITEMS.some((it) => isActive("service", { slug: it.slug }))
    ? "services"
    : PROJECT_ITEMS.some((it) => isActive("project", { slug: it.slug }))
    ? "projects"
    : STACK_ITEMS.some((it) => isActive("techstack", { slug: it.slug }))
    ? "techstacks"
    : null;
  const [openSection, setOpenSection] = useState(activeDefault);
  const toggleSection = (id) => setOpenSection((o) => (o === id ? null : id));

  const switchLang = () => {
    const p = buildPath(page.routeKey, other, page.params);
    if (onNavigate) onNavigate();
    navigate(p);
  };

  const socials = [
    { icon: LinkedinLogo, url: profile?.linkedin, name: "LinkedIn" },
    { icon: XLogo, url: "https://x.com/MagnusLSchmidt", name: "X" },
    { icon: Article, url: "https://magnuslvschmidt.substack.com/", name: "Substack" },
    { icon: MediumLogo, url: "https://medium.com/@MagnusSchmidt", name: "Medium" },
    { icon: InstagramLogo, url: "https://www.instagram.com/magnusloeve/", name: "Instagram" },
    { icon: YoutubeLogo, url: "https://www.youtube.com/@Magnusloevschmidt", name: "YouTube" },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="p-6 pb-4">
        <Link to={link("home")} onClick={onNavigate} data-testid="sidebar-logo" className="flex items-center gap-3">
          <div className="grayscale-hover h-11 w-11 shrink-0 overflow-hidden rounded-full border border-border">
            <img src={profile?.avatar} alt="Magnus Løv Schmidt — SEO Specialist" className="h-full w-full object-cover" />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-[15px] font-bold tracking-tight">Magnus Løv</div>
            <div className="label-mono text-[10px] text-muted-foreground">SEO Specialist</div>
          </div>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        <div className="label-mono px-3 py-2 text-[9px] text-muted-foreground/60">{t("section_explore")}</div>
        <TopLink to={link("home")} active={isActive("home")} icon={House} label={t("nav_home")} testid="nav-home" onNavigate={onNavigate} />
        <TopLink to={link("about")} active={isActive("about")} icon={User} label={t("nav_about")} testid="nav-about" onNavigate={onNavigate} />
        <Section icon={Wrench} label={t("nav_services")} hubTo={link("services")} hubLabel={t("all_services")} items={SERVICE_MENU_ITEMS} routeKey="service" lang={lang} isActive={isActive} onNavigate={onNavigate} testid="nav-services" open={openSection === "services"} onToggle={() => toggleSection("services")} />
        <Section icon={FolderSimple} label={t("nav_projects")} hubTo={link("projects")} hubLabel={t("all_projects")} items={PROJECT_ITEMS} routeKey="project" lang={lang} isActive={isActive} onNavigate={onNavigate} testid="nav-projects" open={openSection === "projects"} onToggle={() => toggleSection("projects")} />
        <Section icon={StackSimple} label={t("nav_techstacks")} hubTo={link("techstacks")} hubLabel={t("all_stacks")} items={STACK_ITEMS} routeKey="techstack" lang={lang} isActive={isActive} onNavigate={onNavigate} testid="nav-techstacks" open={openSection === "techstacks"} onToggle={() => toggleSection("techstacks")} />
        <TopLink to={link("blog")} active={isActive("blog")} icon={Notebook} label={t("nav_blog")} testid="nav-blog" onNavigate={onNavigate} />
        <TopLink to={link("contact")} active={isActive("contact")} icon={EnvelopeSimple} label={t("nav_contact")} testid="nav-contact" onNavigate={onNavigate} />
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-4">
        <div className="mb-3 grid grid-cols-6 gap-1.5">
          {socials.map((s) => (
            <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.name}
               data-testid={`sidebar-social-${s.name.toLowerCase()}`}
               className="group flex aspect-square items-center justify-center rounded-lg border border-border text-muted-foreground transition-all hover:border-foreground hover:bg-foreground hover:text-background">
              <s.icon size={16} weight="fill" />
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="flex flex-1 items-center justify-center gap-2 rounded-md border border-border py-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          <button
            onClick={switchLang}
            data-testid="lang-switch"
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-mono uppercase text-muted-foreground transition-colors hover:text-primary"
          >
            {other}
            <ArrowUpRight size={12} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sidebar({ profile }) {
  return (
    <aside
      data-testid="sidebar"
      className="fixed left-0 top-0 z-40 hidden h-screen w-[280px] border-r border-border bg-background lg:block"
    >
      <SidebarContent profile={profile} />
    </aside>
  );
}
